import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Combobox, userToComboboxOption } from "@/components/ui/Combobox";
import { InfoMessage } from "@/components/ui/InfoMessage";
import { Search, ChevronLeft, Upload, X } from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import { format, parseISO } from "date-fns";
import {
  reviewersMatchSaved,
  shouldRestartWorkflowOnReviewerReassignment,
} from "../../lib/reviewerReassignmentRules";

const EditReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [clients, setClients] = useState([]);
  const [fieldEngineerList, setFieldEngineerList] = useState([]);
  const [peerReviewerList, setPeerReviewerList] = useState([]);
  const [siteReviewerList, setSiteReviewerList] = useState([]);
  const [directorReviewerList, setDirectorReviewerList] = useState([]);
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [showSkeletonField, setShowSkeletonField] = useState(false);
  const [selectedMineType, setSelectedMineType] = useState(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isStageChanging, setIsStageChanging] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const initializedReportIdRef = useRef(null);

  // File upload states
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [isReplacingFile, setIsReplacingFile] = useState(false);

  // Dynamic file types - will be set based on original skeleton file format
  const [allowedFileTypes, setAllowedFileTypes] = useState(['application/msword']);
  const [expectedFileFormat, setExpectedFileFormat] = useState('doc');
  const [expectedFilename, setExpectedFilename] = useState('');

  const [formData, setFormData] = useState({
    client_id: "",
    site_id: "",
    report_name: "",
    field_engineer_id: "",
    peer_reviewer_id: "",
    site_reviewer_id: "",
    director_reviewer_id: "",
    visit_date: "",
    report_stage: "unallocated",
    report_status: "awaiting",
    skeleton_report_name: "",
  });



  // Fetch report data
  const { data: reportResponse, isLoading } = useQuery(["report", id], async () => {
    const response = await api.get(`/reports/${id}`);
    return response.data;
  }, {
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Extract the actual report data
  const reportData = reportResponse?.data || reportResponse;

  const checkDuplicateReport = async (siteId, visitDate) => {
    if (!siteId || !visitDate) return false;

    try {
      const response = await api.get(
        `/reports/check-duplicate?site_id=${siteId}&visit_date=${visitDate}&exclude_id=${id}`
      );
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  // Check for duplicate report names within the same site
  const checkDuplicateReportName = async (reportName, siteId) => {
    if (!reportName || !reportName.trim() || !siteId) return false;

    try {
      const response = await api.get(
        `/reports/check-duplicate-name?report_name=${encodeURIComponent(reportName.trim())}&site_id=${siteId}&exclude_id=${id}`
      );
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  const reportNameMatchesSiteName = (reportName, siteId) => {
    const selectedSite = sites.find((s) => String(s.id) === String(siteId));
    if (!selectedSite?.name) return false;
    return reportName.trim().toLowerCase() === selectedSite.name.trim().toLowerCase();
  };

  const isIdentityFieldsLocked = Boolean(reportData?.id);

  const isReportCompleted =
    reportData?.stage === "completed" || reportData?.status === "completed";

  const isEditDisabled = isReportCompleted;

  const identityFieldsLockedMessage =
    "Client, Site, and Report Name are locked. Create a new report to update them.";

  const completedReportLockedMessage =
    "This report has been completed and is now locked. No further changes can be made.";

  const hasIdentityFieldsChanged = () => {
    if (!reportData) return false;
    const originalName = (reportData.name || "").trim();
    const currentName = (formData.report_name || "").trim();
    return (
      String(formData.client_id) !== String(reportData.client_id) ||
      String(formData.site_id) !== String(reportData.site_id) ||
      currentName !== originalName
    );
  };

  const updateMutation = useMutation((data) => {
    if (data instanceof FormData) {
      return api.put(`/reports/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }

    return api.put(`/reports/${id}`, data);
  }, {
    onMutate: (variables) => {
      // Check if this is a file upload operation
      const isFileUpload = variables instanceof FormData && variables.has('report_file');
      const isFailedStatus = variables.keep_existing_file === 'true' &&
        (formData.report_status.includes('failed_') || formData.report_status.includes('uploaded_'));

      if (isFileUpload || isFailedStatus) {
        setIsProcessingFile(true);
      }
    },
    onSuccess: (response) => {
      setIsProcessingFile(false);
      toast.success(
        response.data?.message ||
        (response.data?.workflowRestarted
          ? "Report updated and workflow restarted from Field Engineer stage"
          : "Report updated successfully")
      );

      queryClient.invalidateQueries(["reports"]);
      queryClient.invalidateQueries(["report", id]);

      const updatedReport = response.data.data || response.data;
      queryClient.setQueryData(["report", id], { data: updatedReport });

      // Update form data with the new values to reflect changes immediately
      if (updatedReport) {
        setFormData(prev => ({
          ...prev,
          client_id: updatedReport.client_id || prev.client_id,
          site_id: updatedReport.site_id || prev.site_id,
          report_name: updatedReport.name || prev.report_name,
          field_engineer_id: updatedReport.field_engineer_id || prev.field_engineer_id,
          peer_reviewer_id: updatedReport.peer_reviewer_id || prev.peer_reviewer_id,
          site_reviewer_id: updatedReport.site_reviewer_id || prev.site_reviewer_id,
          director_reviewer_id: updatedReport.director_reviewer_id || prev.director_reviewer_id,
          visit_date: updatedReport.visited_date
            ? (() => {
              const date = new Date(updatedReport.visited_date);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              return `${year}-${month}-${day}`;
            })()
            : prev.visit_date,
          report_stage: updatedReport.stage || prev.report_stage,
          report_status: updatedReport.status || prev.report_status,
        }));
      }

      setUploadedFile(null);
      setIsReplacingFile(false);
      navigate("/reports");
    },
    onError: (error) => {
      setIsProcessingFile(false);
      if (error.response?.data?.code === 'DUPLICATE_CONTENT') {
        toast.error(error.response.data.message || "Please upload an updated file with different content");
      } else if (error.response?.data?.code === 'FORMAT_MISMATCH') {
        toast.error(error.response.data.message || "File format does not match the expected format");
      } else {
        toast.error(error.response?.data?.message || "Failed to update report");
      }
    },
  });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get("/clients/all-active-clients");
        let clientList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          clientList = res.data.data;
        } else if (Array.isArray(res.data)) {
          clientList = res.data;
        }
        setClients(clientList);
      } catch (err) {
        toast.error("Failed to load clients");
        setClients([]);
      }
    };

    const fetchSites = async () => {
      try {
        const res = await api.get("/sites");
        if (res.data?.data && Array.isArray(res.data.data)) {
          setSites(res.data.data);
          setFilteredSites(res.data.data);
        }
      } catch (err) {
        toast.error("Failed to load sites");
        setSites([]);
        setFilteredSites([]);
      }
    };

    const fetchFieldEngineers = async () => {
      try {
        const res = await api.get("/users/all-field-engineer-users");
        let engineerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          engineerList = res.data.data.filter(
            (user) => user.role === "field-engineer" || user.role_name === "Field Engineer"
          );
        }
        setFieldEngineerList(engineerList);
      } catch (err) {
        toast.error("Failed to load field engineers");
        setFieldEngineerList([]);
      }
    };

    const fetchPeerReviewers = async () => {
      try {
        const res = await api.get("/users/all-peer-reviewer-users");
        let reviewerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          reviewerList = res.data.data.filter(
            (user) => user.role === "peer-reviewer" || user.role_name === "Peer Reviewer"
          );
        }
        setPeerReviewerList(reviewerList);
      } catch (err) {
        toast.error("Failed to load peer reviewers");
        setPeerReviewerList([]);
      }
    };

    const fetchSiteReviewers = async () => {
      try {
        const res = await api.get("/users/all-site-reviewer-users");
        let reviewerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          reviewerList = res.data.data.filter(
            (user) => user.role === "site-reviewer" || user.role_name === "Site Reviewer"
          );
        }
        setSiteReviewerList(reviewerList);
      } catch (err) {
        toast.error("Failed to load site reviewers");
        setSiteReviewerList([]);
      }
    };

    const fetchDirectorReviewers = async () => {
      try {
        const res = await api.get("/users/all-director-reviewer-users");
        let reviewerList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          reviewerList = res.data.data.filter(
            (user) => user.role === "director-reviewer" || user.role_name === "Director Reviewer"
          );
        }
        setDirectorReviewerList(reviewerList);
      } catch (err) {
        toast.error("Failed to load director reviewers");
        setDirectorReviewerList([]);
      }
    };

    fetchClients();
    fetchSites();
    fetchFieldEngineers();
    fetchPeerReviewers();
    fetchSiteReviewers();
    fetchDirectorReviewers();
  }, []);

  const getExpectedFileFormat = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/original-format`);
      const extension = response.data.originalFormat.toLowerCase();
      setExpectedFileFormat(extension);

      // Set allowed MIME types based on extension
      const mimeTypes = {
        'doc': ['application/msword'],
        'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        'pdf': ['application/pdf']
      };

      setAllowedFileTypes(mimeTypes[extension] || ['application/msword']);
    } catch (error) {
      setExpectedFileFormat('doc');
      setAllowedFileTypes(['application/msword']);
    }
  };

  const cancelFileReplacement = () => {
    setIsReplacingFile(false);
    setUploadedFile(null);
    toast.success("File replacement cancelled. Keeping existing file.");
  };


  const handleViewExistingFile = async () => {
    if (!existingFile) {
      toast.error("No file to view");
      return;
    }

    try {
      // Get signed URL for viewing the existing file
      const response = await api.get(`/reports/${id}/view-existing-file`, {
        params: existingFile.filePath ? { file_path: existingFile.filePath } : undefined,
      });

      if (response.data.success && response.data.url) {
        const fileUrl = response.data.url;
        const newWindow = window.open(fileUrl, "_blank");

        if (!newWindow) {
          toast.error("Popup blocked. Please allow popups for this site.", { id: "view-existing-file" });
          return;
        }

        toast.success("File opened successfully", { id: "view-existing-file" });
      } else {
        toast.error("Failed to load file", { id: "view-existing-file" });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load file", { id: "view-existing-file" });
    }
  };

  // Function to get expected filename for a report
  const getExpectedFilename = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/expected-filename`);
      setExpectedFilename(response.data.expectedFilename);
    } catch (error) {
      setExpectedFilename('');
    }
  };

  // Function to check if there's an existing uploaded file
  const checkExistingFile = async (reportId, desiredStatus = null) => {
    // Don't check for existing file if we're in the middle of a stage change
    if (isStageChanging) {
      return;
    }

    try {
      const url = desiredStatus
        ? `/reports/${reportId}/existing-file?desired_status=${desiredStatus}`
        : `/reports/${reportId}/existing-file`;

      const response = await api.get(url);

      if (response.data.hasFile) {
        setExistingFile({
          filename: response.data.filename,
          uploadedAt: response.data.uploadedAt,
          uploadedBy: response.data.uploadedBy,
          failedStatus: response.data.failedStatus || null
        });
        setIsReplacingFile(false);
      } else {
        setExistingFile(null);
        setIsReplacingFile(false);
      }
    } catch (error) {
      setExistingFile(null);
      setIsReplacingFile(false);
    }
  };

  const getSavedReviewers = () => ({
    field_engineer_id: reportData?.field_engineer_id ?? "",
    peer_reviewer_id: reportData?.peer_reviewer_id ?? "",
    site_reviewer_id: reportData?.site_reviewer_id ?? "",
    director_reviewer_id: reportData?.director_reviewer_id ?? "",
  });

  const applyReviewerAssignmentEffects = (reviewerUpdates) => {
    if (!reportData?.id) return;

    const nextReviewers = {
      ...getSavedReviewers(),
      field_engineer_id: formData.field_engineer_id,
      peer_reviewer_id: formData.peer_reviewer_id,
      site_reviewer_id: formData.site_reviewer_id,
      director_reviewer_id: formData.director_reviewer_id,
      ...reviewerUpdates,
    };

    const savedReviewers = getSavedReviewers();

    setUploadedFile(null);
    setIsReplacingFile(false);

    if (reviewersMatchSaved(savedReviewers, nextReviewers)) {
      setFormData((prev) => ({
        ...prev,
        ...nextReviewers,
        report_stage: reportData.stage || "unallocated",
        report_status: reportData.status || "awaiting",
      }));
      checkExistingFile(id, reportData.status);
      return;
    }

    const { shouldRestart } = shouldRestartWorkflowOnReviewerReassignment({
      existingReviewers: savedReviewers,
      newReviewers: nextReviewers,
      currentStage: reportData.stage,
      currentStatus: reportData.status,
      latestFailedStatus: reportData.latest_failed_status ?? null,
      reviewActivityStatuses: reportData.review_activity_statuses ?? [],
    });

    if (shouldRestart) {
      setExistingFile(null);
      setFormData((prev) => ({
        ...prev,
        ...nextReviewers,
        report_stage: "field_engineer",
        report_status: "awaiting_field_engineer",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...nextReviewers,
      report_stage: reportData.stage || prev.report_stage,
      report_status: reportData.status || prev.report_status,
    }));
    checkExistingFile(id, reportData.status);
  };

  // Set form data when report and all lists are loaded (once per report visit)
  useEffect(() => {
    initializedReportIdRef.current = null;
  }, [id]);

  useEffect(() => {
    if (
      reportData &&
      clients.length > 0 &&
      sites.length > 0 &&
      fieldEngineerList.length > 0 &&
      peerReviewerList.length > 0 &&
      siteReviewerList.length > 0 &&
      directorReviewerList.length > 0
    ) {
      if (initializedReportIdRef.current === reportData.id) {
        return;
      }

      initializedReportIdRef.current = reportData.id;

      const newFormData = {
        client_id: reportData.client_id || "",
        site_id: reportData.site_id || "",
        report_name: reportData.name || "",
        field_engineer_id: reportData.field_engineer_id || "",
        peer_reviewer_id: reportData.peer_reviewer_id || "",
        site_reviewer_id: reportData.site_reviewer_id || "",
        director_reviewer_id: reportData.director_reviewer_id || "",
        visit_date: reportData.visited_date
          ? (() => {
            const date = new Date(reportData.visited_date);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          })()
          : "",
        report_stage: reportData.stage || "unallocated",
        report_status: reportData.status || "awaiting",
      };

      setFormData(newFormData);

      const site = sites.find((s) => String(s.id) === String(reportData.site_id));
      if (site) {
        const hasSkeleton = !!site.has_skeleton && !!site.skeleton_file_path;
        setShowSkeletonField(hasSkeleton);
        if (hasSkeleton) {
          setSelectedMineType({
            id: site.mine_id,
            name: site.mine_type,
            file_path: site.skeleton_file_path || null,
            has_skeleton: hasSkeleton,
          });
        } else {
          setSelectedMineType(null);
        }
      } else {
        setShowSkeletonField(false);
        setSelectedMineType(null);
      }

      // Filter sites based on selected client
      if (reportData.client_id && sites.length > 0) {
        const clientSites = sites.filter((site) => site.client_id === reportData.client_id);
        setFilteredSites(clientSites);
      }

      // Get expected file format and filename for this report
      if (reportData.id) {
        Promise.all([
          getExpectedFileFormat(reportData.id),
          getExpectedFilename(reportData.id)
        ]).catch(error => {
          console.error('Error loading file metadata:', error);
        });
      }
    }
  }, [
    reportData,
    clients,
    sites,
    fieldEngineerList,
    peerReviewerList,
    siteReviewerList,
    directorReviewerList,
  ]);

  useEffect(() => {
    if (reportData && sites.length > 0 && reportData.client_id) {
      const clientSites = sites.filter((site) => site.client_id === reportData.client_id);
      setFilteredSites(clientSites);
    }
  }, [reportData, sites]);


  useEffect(() => {
    if (reportData?.id && formData.report_status && shouldShowFileUpload()) {
      checkExistingFile(reportData.id);
    }
  }, [reportData?.id, formData.report_status]);

  useEffect(() => {
    // Always reset file states when stage or status changes
    setUploadedFile(null);
    setIsReplacingFile(false);
    setExistingFile(null);

    // Only check for existing file if the new status requires file upload and we're not changing stages
    if (formData.report_status && shouldShowFileUpload() && !isStageChanging) {
      checkExistingFile(id, formData.report_status);
    }
  }, [formData.report_status, formData.report_stage, id, isStageChanging]);

  const handleInputChange = (name, value) => {
    if (isEditDisabled) {
      return;
    }

    if (name === "report_name" && isIdentityFieldsLocked) {
      return;
    }

    if (name === "report_status") {
      // Immediately reset all file states when status changes
      setUploadedFile(null);
      setIsReplacingFile(false);
      setExistingFile(null);
    }

    if (name === "report_status" && value.includes('failed_')) {
      checkExistingFile(id, value);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    if (isEditDisabled) {
      return;
    }

    if ((name === "client" || name === "site") && isIdentityFieldsLocked) {
      return;
    }

    if (name === "client") {
      const clientSites = sites.filter((site) => site.client_id === parseInt(value));
      setFilteredSites(clientSites);
      setShowSkeletonField(false);
      setSelectedMineType(null);
      setFormData((prev) => ({ ...prev, client_id: value, site_id: "" }));
    } else if (name === "fieldEngineer") {
      if (!value) {
        setUploadedFile(null);
        setIsReplacingFile(false);
        setExistingFile(null);
        setFormData((prev) => ({
          ...prev,
          field_engineer_id: "",
          report_stage: "unallocated",
          report_status: "awaiting",
        }));
      } else {
        applyReviewerAssignmentEffects({ field_engineer_id: value });
      }
    } else if (name === "peerReviewer") {
      if (!value && formData.report_stage === "peer_reviewer") {
        setUploadedFile(null);
        setIsReplacingFile(false);
        setExistingFile(null);
        setFormData((prevState) => ({
          ...prevState,
          peer_reviewer_id: "",
          report_stage: "unallocated",
          report_status: "awaiting",
        }));
        toast.success("Stage reset to UNALLOCATED because Peer Reviewer was removed");
      } else {
        applyReviewerAssignmentEffects({ peer_reviewer_id: value || "" });
      }

      if (value && formData.report_stage === "unallocated" && !formData.peer_reviewer_id) {
        toast.success("Peer Reviewer added! You can now set the stage to Peer Reviewer.");
      }
    } else if (name === "siteReviewer") {
      if (!value && formData.report_stage === "site_reviewer") {
        setUploadedFile(null);
        setIsReplacingFile(false);
        setExistingFile(null);
        setFormData((prevState) => ({
          ...prevState,
          site_reviewer_id: "",
          report_stage: "unallocated",
          report_status: "awaiting",
        }));
        toast.success("Stage reset to UNALLOCATED because Site Reviewer was removed");
      } else {
        applyReviewerAssignmentEffects({ site_reviewer_id: value || "" });
      }

      if (value && formData.report_stage === "unallocated" && !formData.site_reviewer_id) {
        toast.success("Site Reviewer added! You can now set the stage to Site Reviewer.");
      }
    } else if (name === "directorReviewer") {
      if (!value && formData.report_stage === "director_reviewer") {
        setUploadedFile(null);
        setIsReplacingFile(false);
        setExistingFile(null);
        setFormData((prevState) => ({
          ...prevState,
          director_reviewer_id: "",
          report_stage: "unallocated",
          report_status: "awaiting",
        }));
        toast.success("Stage reset to UNALLOCATED because Director Reviewer was removed");
      } else {
        applyReviewerAssignmentEffects({ director_reviewer_id: value || "" });
      }

      if (value && formData.report_stage === "unallocated" && !formData.director_reviewer_id) {
        toast.success("Director Reviewer added! You can now set the stage to Director Reviewer.");
      }
    } else if (name === "site") {
      const site = sites.find((s) => String(s.id) === String(value));
      if (site) {
        const hasSkeleton = !!site.has_skeleton && !!site.skeleton_file_path;
        setShowSkeletonField(hasSkeleton);
        if (hasSkeleton) {
          setSelectedMineType({
            id: site.mine_id,
            name: site.mine_type,
            file_path: site.skeleton_file_path,
          });
          toast.success(`Skeleton template available: ${site.mine_type}`);
        } else {
          setSelectedMineType(null);
          toast.success("No skeleton template available for this site");
        }
      } else {
        setShowSkeletonField(false);
        setSelectedMineType(null);
      }
      setFormData((prev) => ({ ...prev, site_id: value }));
    } else if (name === "report_stage") {
      // Set flag to prevent race conditions during stage change
      setIsStageChanging(true);

      // Immediately reset all file states when stage changes
      setUploadedFile(null);
      setIsReplacingFile(false);
      setExistingFile(null);

      let actualStage = value;
      if (value === "failed_peer_review" ||
        value === "failed_site_review" ||
        value === "failed_director_review") {
        actualStage = "field_engineer";
      }

      if (actualStage === "peer_reviewer" && !formData.peer_reviewer_id) {
        toast.error("Please select a Peer Reviewer before setting stage to Peer Reviewer");
        return;
      }
      if (actualStage === "site_reviewer" && !formData.site_reviewer_id) {
        toast.error("Please select a Site Reviewer before setting stage to Site Reviewer");
        return;
      }
      if (actualStage === "director_reviewer" && !formData.director_reviewer_id) {
        toast.error("Please select a Director Reviewer before setting stage to Director Reviewer");
        return;
      }

      let defaultStatus = "awaiting";
      if (actualStage === "field_engineer") {
        if (reportData?.status === "uploaded_peer_review") {
          defaultStatus = "failed_peer_review";
        } else if (reportData?.status === "uploaded_site_review") {
          defaultStatus = "failed_site_review";
        } else if (reportData?.status === "uploaded_director_review") {
          defaultStatus = "failed_director_review";
        } else {
          if (reportData?.status === "uploaded_field_engineer") {
            defaultStatus = "uploaded_field_engineer";
          } else {
            defaultStatus = "awaiting_field_engineer";
          }
        }
      } else if (actualStage === "peer_reviewer") {
        if (reportData?.status === "uploaded_peer_review") {
          defaultStatus = "uploaded_peer_review";
        } else {
          defaultStatus = "awaiting_peer_reviewer";
        }
      } else if (actualStage === "site_reviewer") {
        if (reportData?.status === "uploaded_site_review") {
          defaultStatus = "uploaded_site_review";
        } else {
          defaultStatus = "awaiting_site_reviewer";
        }
      } else if (actualStage === "director_reviewer") {
        if (reportData?.status === "uploaded_director_review") {
          defaultStatus = "uploaded_director_review";
        } else {
          defaultStatus = "awaiting_director_reviewer";
        }
      } else if (actualStage === "completed") {
        defaultStatus = "completed";
      }
      setFormData((prev) => ({ ...prev, [name]: actualStage, report_status: defaultStatus }));

      // Clear the stage changing flag after a short delay to allow state updates to complete
      setTimeout(() => {
        setIsStageChanging(false);
      }, 100);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditDisabled) {
      toast.error(completedReportLockedMessage);
      return;
    }

    const allFieldsEmpty =
      !formData.report_name.trim() &&
      !formData.client_id &&
      !formData.site_id &&
      !formData.field_engineer_id &&
      !formData.peer_reviewer_id &&
      !formData.site_reviewer_id &&
      !formData.director_reviewer_id &&
      !formData.visit_date.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before updating");
      return;
    }

    let hasErrors = false;

    if (!formData.report_name.trim()) {
      toast.error("Report name is required");
      hasErrors = true;
    }

    if (!formData.client_id) {
      toast.error("Please select Client");
      hasErrors = true;
    }

    if (!formData.site_id) {
      toast.error("Please select Site");
      hasErrors = true;
    }
    // Peer, Site, and Director reviewers are optional

    if (!formData.visit_date.trim()) {
      toast.error("Visit date is required");
      hasErrors = true;
    }

    const isStatusChangingToUploaded = reportData.status !== formData.report_status &&
      formData.report_status.includes('uploaded_');

    const isStatusChangingToFailed = reportData.status !== formData.report_status &&
      formData.report_status.includes('failed_');

    const needsFileForUploadedStatus = isStatusChangingToUploaded && !existingFile;
    const needsFileForFailedStatus = isStatusChangingToFailed && !existingFile;

    if ((needsFileForUploadedStatus || needsFileForFailedStatus || isReplacingFile) && !uploadedFile) {
      if (needsFileForFailedStatus) {
        toast.error("File upload is required when setting status to failed review");
      } else if (needsFileForUploadedStatus) {
        toast.error("File upload is required when setting status to uploaded");
      } else {
        toast.error("Please upload a new file to replace the existing one");
      }
      hasErrors = true;
    }

    if (!isFileUploadMandatory() && uploadedFile && !isReplacingFile) {
      toast.error("File upload is not required at this stage. Please remove the file or change the status.");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    if (hasIdentityFieldsChanged()) {
      toast.error(identityFieldsLockedMessage);
      return;
    }

    if (reportNameMatchesSiteName(formData.report_name, formData.site_id)) {
      toast.error("Report name cannot match the selected site name.");
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const isDuplicateName = await checkDuplicateReportName(formData.report_name, formData.site_id);
      if (isDuplicateName) {
        toast.error(
          "A report with this name already exists for this site. Please choose a different report name."
        );
        setIsCheckingDuplicate(false);
        return;
      }
    } catch (error) {
      toast.error("Error checking for duplicate report names. Please try again.");
      setIsCheckingDuplicate(false);
      return;
    }

    try {
      const isDuplicate = await checkDuplicateReport(formData.site_id, formData.visit_date);
      if (isDuplicate) {
        toast.error(
          "A report already exists for this site on the selected date. Please choose a different date or site."
        );
        setIsCheckingDuplicate(false);
        return;
      }
    } catch (error) {
      toast.error("Error checking for duplicate reports. Please try again.");
      setIsCheckingDuplicate(false);
      return;
    }
    setIsCheckingDuplicate(false);

    // Prepare submission data
    const submissionData = {
      client_id: formData.client_id,
      site_id: formData.site_id,
      report_name: formData.report_name,
      field_engineer_id: formData.field_engineer_id || null,
      peer_reviewer_id: formData.peer_reviewer_id || null,
      site_reviewer_id: formData.site_reviewer_id || null,
      director_reviewer_id: formData.director_reviewer_id || null,
      visit_date: formData.visit_date,
      report_stage: formData.report_stage || "unallocated",
      report_status: formData.report_status || "awaiting"
    };


    if (uploadedFile) {
      const submitFormData = new FormData();

      Object.keys(submissionData).forEach(key => {
        if (submissionData[key] !== null) {
          submitFormData.append(key, submissionData[key]);
        }
      });

      submitFormData.append('report_file', uploadedFile);
      submitFormData.append('is_replacing_file', 'true');

      updateMutation.mutate(submitFormData);
    } else {
      if (existingFile && !isReplacingFile) {
        submissionData.keep_existing_file = 'true';
      }

      updateMutation.mutate(submissionData);
    }
  };

  // File upload helper functions
  const shouldShowFileUpload = () => {
    if (isReportCompleted) {
      return false;
    }

    const status = formData.report_status;
    const allowedStatuses = [
      "uploaded_field_engineer",
      "uploaded_peer_review",
      "uploaded_site_review",
      "uploaded_director_review",
      "failed_peer_review",
      "failed_site_review",
      "failed_director_review",
    ];

    const shouldShow = allowedStatuses.includes(status);
    return shouldShow;
  };

  const isFileUploadMandatory = () => {
    const status = formData.report_status;

    const mandatoryUploadStatuses = [
      "uploaded_field_engineer",
      "uploaded_peer_review",
      "uploaded_site_review",
      "uploaded_director_review",
      "failed_peer_review",
      "failed_site_review",
      "failed_director_review"
    ];

    if (status.includes('failed_')) {
      return true;
    }

    if (!existingFile) {
      const isMandatory = mandatoryUploadStatuses.includes(status);
      return isMandatory;
    }

    if (existingFile && mandatoryUploadStatuses.includes(status)) {
      const result = isReplacingFile;
      return result;
    }

    if (existingFile && existingFile.failedStatus && !mandatoryUploadStatuses.includes(status)) {
      return true;
    }

    return false;
  };

  // Check if download button should be shown
  const shouldShowDownloadButton = () => {
    if (isReportCompleted || isWorkflowRestartPreview()) {
      return false;
    }

    const status = reportData?.status || formData.report_status;

    const downloadStatuses = [
      "awaiting_field_engineer",
      "awaiting_peer_reviewer",
      "awaiting_site_reviewer",
      "awaiting_director_reviewer"
    ];
    return downloadStatuses.includes(status);
  };

  // Check if reject button should be shown
  const shouldShowRejectButton = () => {
    if (isReportCompleted || isWorkflowRestartPreview()) {
      return false;
    }

    const status = reportData?.status || formData.report_status;
    const selectedStatus = formData.report_status;

    const rejectStatuses = [
      "uploaded_peer_review",
      "uploaded_site_review",
      "uploaded_director_review"
    ];

    // Only show reject button if both current status and selected status match
    return status === selectedStatus && rejectStatuses.includes(status);
  };

  // Check if complete button should be shown
  const shouldCompleteButton = () => {
    if (isReportCompleted || isWorkflowRestartPreview()) {
      return false;
    }

    const status = reportData?.status || formData.report_status;
    const selectedStatus = formData.report_status;

    const completeStatuses = [
      "uploaded_peer_review",
      "uploaded_site_review",
      "uploaded_director_review"
    ];
    return status === selectedStatus && completeStatuses.includes(status);
  }

  // Handle download and status update
  const handleDownloadAndStart = async () => {
    if (isEditDisabled) {
      return;
    }

    const status = formData.report_status;
    let newStatus = "";
    let newStage = "";

    switch (status) {
      case "awaiting_field_engineer":
        newStatus = "started_field_engineer";
        newStage = "field_engineer";
        break;
      case "awaiting_peer_reviewer":
        newStatus = "started_peer_review";
        newStage = "peer_reviewer";
        break;
      case "awaiting_site_reviewer":
        newStatus = "started_site_review";
        newStage = "site_reviewer";
        break;
      case "awaiting_director_reviewer":
        newStatus = "started_director_review";
        newStage = "director_reviewer";
        break;
      default:
        toast.error("Invalid status for download");
        return;
    }

    setIsDownloading(true);
    try {
      const response = await api.get(`/reports/${id}/download-latest`);

      if (response.data.success && response.data.url) {
        // Download the file
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setFormData(prev => ({
          ...prev,
          report_status: newStatus,
          report_stage: newStage
        }));

        queryClient.invalidateQueries(["report", id]);

        toast.success("File downloaded and status updated to started");
      } else {
        toast.error("Failed to download file");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle complete functionality
  const handleComplete = async () => {
    if (isEditDisabled) {
      return;
    }

    const status = formData.report_status;

    // Validate that we have a valid status for completion
    const allowedStatuses = ["uploaded_peer_review", "uploaded_site_review", "uploaded_director_review"];
    if (!allowedStatuses.includes(status)) {
      toast.error("Invalid status for completion");
      return;
    }

    try {
      setIsProcessingFile(true);
      // Send reviewer IDs from form data
      const requestData = {
        site_reviewer_id: formData.site_reviewer_id || null,
        director_reviewer_id: formData.director_reviewer_id || null
      };

      const response = await api.post(`/reports/${id}/complete`, requestData);

      if (response.data.success) {
        toast.success(response.data.message || "Report completed successfully!", { id: "complete-report" });

        // Update form data to reflect the new status
        setFormData(prev => ({
          ...prev,
          report_stage: response.data.newStage,
          report_status: response.data.newStatus
        }));

        // Invalidate queries to refresh data
        queryClient.invalidateQueries(["report", id]);
        queryClient.invalidateQueries(["reports"]);

        navigate("/reports");
      } else {
        toast.error("Failed to complete report", { id: "complete-report" });
      }
    } catch (error) {
      if (error.response?.data?.error === 'Site reviewer required' || error.response?.data?.error === 'Director reviewer required') {
        toast.error(error.response.data.message || "Required reviewer has not been selected", { id: "complete-report" });
      } else {
        toast.error(error.response?.data?.message || "Failed to complete report", { id: "complete-report" });
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  // Handle reject functionality
  const handleReject = async () => {
    if (isEditDisabled) {
      return;
    }

    const status = formData.report_status;

    // Validate that we have a valid status for rejection
    const allowedStatuses = ["uploaded_peer_review", "uploaded_site_review", "uploaded_director_review"];
    if (!allowedStatuses.includes(status)) {
      toast.error("Invalid status for rejection");
      return;
    }

    try {
      setIsProcessingFile(true);
      const response = await api.post(`/reports/${id}/reject`);

      if (response.data.success) {
        toast.success("Report rejected and reassigned to Field Engineer!", { id: "reject-report" });

        // Update form data to reflect the new status
        setFormData(prev => ({
          ...prev,
          report_stage: "field_engineer",
          report_status: "awaiting_field_engineer"
        }));

        // Invalidate queries to refresh data
        queryClient.invalidateQueries(["report", id]);
        queryClient.invalidateQueries(["reports"]);

        // Navigate to reports page after successful rejection
        navigate("/reports");
      } else {
        toast.error("Failed to reject report", { id: "reject-report" });
      }
    } catch (error) {
      // Handle specific rejection validation errors
      if (error.response?.data?.code === 'NO_FILE_UPLOADED') {
        toast.error(error.response.data.message || "The reviewer must upload a file with feedback before rejecting", { id: "reject-report" });
      } else if (error.response?.data?.code === 'NO_CHANGES_DETECTED') {
        toast.error(error.response.data.message || "The reviewer must make changes to the file before rejecting", { id: "reject-report" });
      } else if (error.response?.data?.code === 'NO_COMPARISON_FILE_FOUND') {
        toast.error(error.response.data.message || "System error: No comparison file found", { id: "reject-report" });
      } else {
        toast.error(error.response?.data?.message || "Failed to reject report", { id: "reject-report" });
      }
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleFiles = (files) => {

    if (uploadedFile) {
      toast.error("A file is already selected. Please remove it first to upload a different file.");
      return;
    }

    if (!isFileUploadMandatory() && !isReplacingFile) {
      toast.error("File upload is not required at this stage. Please check the current status.");
      return;
    }

    const maxFileSize = 25 * 1024 * 1024; // 25MB in bytes

    const validFiles = files.filter((file) => {
      // Check file type
      if (!allowedFileTypes.includes(file.type)) {
        const formatText = expectedFileFormat ? `${expectedFileFormat.toUpperCase()} files` : 'DOC files';
        toast.error(`Only ${formatText} are allowed for this report.`);
        return false;
      }

      // Check file size
      if (file.size > maxFileSize) {
        toast.error(`File too large. Maximum size is 25MB.`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) {
      return;
    }

    // Store the first valid file
    setUploadedFile(validFiles[0]);
    toast.success("File uploaded successfully!");
  };

  const handleViewUploadedFile = (file) => {
    if (!file) {
      toast.error("No file to view");
      return;
    }

    try {
      const fileUrl = URL.createObjectURL(file);
      const newWindow = window.open(fileUrl, "_blank");

      if (!newWindow) {
        toast.error("Popup blocked. Please allow popups for this site.");
        URL.revokeObjectURL(fileUrl);
        return;
      }

      newWindow.onbeforeunload = () => {
        URL.revokeObjectURL(fileUrl);
      };

      setTimeout(() => {
        if (newWindow.closed) {
          URL.revokeObjectURL(fileUrl);
        }
      }, 30000);

      toast.success("File opened successfully");
    } catch (error) {
      toast.error("Failed to open file");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };



  const isWorkflowRestartPreview = () =>
    formData.report_stage === "field_engineer" &&
    formData.report_status === "awaiting_field_engineer" &&
    (reportData?.stage !== "field_engineer" ||
      reportData?.status !== "awaiting_field_engineer");

  // Get status options based on selected stage and current status
  const getStatusOptions = (stage, currentStatus) => {
    if (isWorkflowRestartPreview()) {
      return [{ value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" }];
    }

    const savedStage = reportData?.stage || formData.report_stage;
    const savedStatus = reportData?.status || formData.report_status;

    // Check if workflow has started (field engineer stage has been reached)
    const hasWorkflowStarted = savedStage !== "unallocated" ||
      (savedStatus && savedStatus !== "awaiting");

    switch (stage) {
      case "unallocated":
        // Only show awaiting if workflow hasn't started
        if (!hasWorkflowStarted) {
          return [{ value: "awaiting", label: "AWAITING" }];
        }
        // If workflow has started, don't allow going back to unallocated
        return [];
      case "field_engineer":
        if (savedStatus === "awaiting") {
          return [
            { value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" },
          ];
        }
        if (savedStatus === "uploaded_field_engineer") {
          return [
            { value: "uploaded_field_engineer", label: "UPLOADED FIELD ENGINEER" },
          ];
        }
        if (savedStatus === "uploaded_peer_review") {
          return [
            { value: "failed_peer_review", label: "FAILED PEER REVIEW" },
          ];
        }
        if (savedStatus === "uploaded_site_review") {
          return [
            { value: "failed_site_review", label: "FAILED SITE REVIEW" },
          ];
        }
        if (savedStatus === "uploaded_director_review") {
          return [
            { value: "failed_director_review", label: "FAILED DIRECTOR REVIEW" },
          ];
        }
        if (currentStatus === "started_field_engineer" || currentStatus === "uploaded_field_engineer") {
          return [
            { value: "started_field_engineer", label: "STARTED FIELD ENGINEER" },
            { value: "uploaded_field_engineer", label: "UPLOADED FIELD ENGINEER" },
          ];
        }
        if (savedStatus === "failed_peer_review") {
          return [
            { value: "failed_peer_review", label: "FAILED PEER REVIEW" },
            { value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" },
          ];
        }
        if (savedStatus === "failed_site_review") {
          return [
            { value: "failed_site_review", label: "FAILED SITE REVIEW" },
            { value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" },
          ];
        }
        if (savedStatus === "failed_director_review") {
          return [
            { value: "failed_director_review", label: "FAILED DIRECTOR REVIEW" },
            { value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" },
          ];
        }
        return [
          { value: "awaiting_field_engineer", label: "AWAITING FIELD ENGINEER" },
          { value: "started_field_engineer", label: "STARTED FIELD ENGINEER" },
          { value: "uploaded_field_engineer", label: "UPLOADED FIELD ENGINEER" },
        ];
      case "peer_reviewer":
        if (savedStatus === "uploaded_peer_review") {
          return [
            { value: "uploaded_peer_review", label: "UPLOADED PEER REVIEW" },
          ];
        }
        if (currentStatus === "started_peer_review" || currentStatus === "uploaded_peer_review") {
          return [
            { value: "started_peer_review", label: "STARTED PEER REVIEW" },
            { value: "uploaded_peer_review", label: "UPLOADED PEER REVIEW" },
          ];
        }
        return [
          { value: "awaiting_peer_reviewer", label: "AWAITING PEER REVIEWER" },
        ];
      case "site_reviewer":
        if (savedStatus === "uploaded_site_review") {
          return [
            { value: "uploaded_site_review", label: "UPLOADED SITE REVIEW" },
          ];
        }
        if (currentStatus === "started_site_review" || currentStatus === "uploaded_site_review") {
          return [
            { value: "started_site_review", label: "STARTED SITE REVIEW" },
            { value: "uploaded_site_review", label: "UPLOADED SITE REVIEW" },
          ];
        }
        return [
          { value: "awaiting_site_reviewer", label: "AWAITING SITE REVIEWER" },
        ];
      case "director_reviewer":
        if (savedStatus === "uploaded_director_review") {
          return [
            { value: "uploaded_director_review", label: "UPLOADED DIRECTOR REVIEW" },
          ];
        }
        if (currentStatus === "started_director_review" || currentStatus === "uploaded_director_review") {
          return [
            { value: "started_director_review", label: "STARTED DIRECTOR REVIEW" },
            { value: "uploaded_director_review", label: "UPLOADED DIRECTOR REVIEW" },
          ];
        }
        return [
          { value: "awaiting_director_reviewer", label: "AWAITING DIRECTOR REVIEWER" },
        ];
      case "completed":
        return [{ value: "completed", label: "COMPLETED" }];
      default:
        // Only show awaiting if workflow hasn't started
        if (!hasWorkflowStarted) {
          return [{ value: "awaiting", label: "AWAITING" }];
        }
        return [];
    }
  };

  const isStageSelectionDisabled = () => {
    if (formData.report_stage === "field_engineer" && !formData.field_engineer_id) {
      return true;
    }
    if (formData.report_stage === "peer_reviewer" && !formData.peer_reviewer_id) {
      return true;
    }
    if (formData.report_stage === "site_reviewer" && !formData.site_reviewer_id) {
      return true;
    }
    if (formData.report_stage === "director_reviewer" && !formData.director_reviewer_id) {
      return true;
    }
    return false;
  };

  const getStageValidationMessage = () => {
    if (formData.report_stage === "peer_reviewer" && !formData.peer_reviewer_id) {
      return "Peer Reviewer must be selected for this stage";
    }
    if (formData.report_stage === "site_reviewer" && !formData.site_reviewer_id) {
      return "Site Reviewer must be selected for this stage";
    }
    if (formData.report_stage === "director_reviewer" && !formData.director_reviewer_id) {
      return "Director Reviewer must be selected for this stage";
    }
    return null;
  };

  // Check if stage and status changes should be disabled based on current status
  const isStageStatusChangeDisabled = () => {
    if (isWorkflowRestartPreview()) {
      return true;
    }

    const status = formData.report_status;
    const stage = formData.report_stage;
    const savedStage = reportData?.stage;
    const savedStatus = reportData?.status;


    if (stage === "completed") {
      return true;
    }


    if (stage !== savedStage) {
      return false;
    }

    if (savedStatus && savedStatus.includes("uploaded_")) {
      return false;
    }

    const disabledStatuses = [
      "awaiting_field_engineer",
      "awaiting_peer_reviewer",
      "awaiting_site_reviewer",
      "awaiting_director_reviewer"
    ];

    const isDisabled = disabledStatuses.includes(status);
    return isDisabled;
  };

  const isStageFieldDisabled = () => {
    if (isWorkflowRestartPreview()) {
      return true;
    }

    if (formData.report_stage === "completed") {
      return true;
    }

    const status = formData.report_status;

    const disabledStatuses = [
      "awaiting_field_engineer",
      "started_field_engineer",
      "awaiting_peer_reviewer",
      "started_peer_review",
      "awaiting_site_reviewer",
      "started_site_review",
      "awaiting_director_reviewer",
      "started_director_review"
    ];

    return disabledStatuses.includes(status);
  };


  const getStageOptions = () => {
    if (isWorkflowRestartPreview()) {
      return [
        {
          value: "field_engineer",
          label: formData.field_engineer_id
            ? "FIELD ENGINEER"
            : "FIELD ENGINEER (No Field Engineer selected)",
          disabled: !formData.field_engineer_id,
        },
      ];
    }

    const savedStage = reportData?.stage;
    const savedStatus = reportData?.status;
    const currentStage = formData.report_stage;
    const currentStatus = formData.report_status;

    if (currentStage === "completed") {
      return [{ value: "completed", label: "COMPLETED" }];
    }

    // Check if workflow has started (field engineer stage has been reached)
    const hasWorkflowStarted =
      savedStage !== "unallocated" || (savedStatus && savedStatus !== "awaiting");

    const baseOptions = [];

    // Only show unallocated if workflow hasn't started
    if (!hasWorkflowStarted) {
      baseOptions.push({ value: "unallocated", label: "UNALLOCATED" });
    }

    // Only show field_engineer if:
    if (
      !hasWorkflowStarted ||
      currentStage === "field_engineer" ||
      savedStage === "field_engineer" ||
      (currentStage === "unallocated" && formData.field_engineer_id)
    ) {
      baseOptions.push({
        value: "field_engineer",
        label: formData.field_engineer_id ? "FIELD ENGINEER" : "FIELD ENGINEER (No Field Engineer selected)",
        disabled: !formData.field_engineer_id,
        invalid: formData.report_stage === "field_engineer" && !formData.field_engineer_id
      });
    }

    // Only show peer_reviewer if:
    if (
      (savedStage === "field_engineer" && savedStatus === "uploaded_field_engineer") ||
      savedStage === "peer_reviewer" ||
      currentStage === "peer_reviewer"
    ) {
      baseOptions.push({
        value: "peer_reviewer",
        label: formData.peer_reviewer_id ? "PEER REVIEWER" : "PEER REVIEWER (No Peer Reviewer selected)",
        disabled: !formData.peer_reviewer_id,
        invalid: formData.report_stage === "peer_reviewer" && !formData.peer_reviewer_id
      });
    }

    // Only show site_reviewer if:
    if (
      (savedStage === "peer_reviewer" && savedStatus === "uploaded_peer_review") ||
      savedStage === "site_reviewer" ||
      currentStage === "site_reviewer"
    ) {
      baseOptions.push({
        value: "site_reviewer",
        label: formData.site_reviewer_id ? "SITE REVIEWER" : "SITE REVIEWER (No Site Reviewer selected)",
        disabled: !formData.site_reviewer_id,
        invalid: formData.report_stage === "site_reviewer" && !formData.site_reviewer_id
      });
    }

    // Only show director_reviewer if:
    if (
      (savedStage === "site_reviewer" && savedStatus === "uploaded_site_review") ||
      savedStage === "director_reviewer" ||
      currentStage === "director_reviewer"
    ) {
      baseOptions.push({
        value: "director_reviewer",
        label: formData.director_reviewer_id ? "DIRECTOR REVIEWER" : "DIRECTOR REVIEWER (No Director Reviewer selected)",
        disabled: !formData.director_reviewer_id,
        invalid: formData.report_stage === "director_reviewer" && !formData.director_reviewer_id
      });
    }

    // Only show completed if current stage is director_reviewer and status is uploaded_director_review
    if (
      (savedStage === "director_reviewer" && savedStatus === "uploaded_director_review") ||
      (currentStage === "director_reviewer" && currentStatus === "uploaded_director_review")
    ) {
      baseOptions.push({ value: "completed", label: "COMPLETED" });
    }

    return baseOptions;
  };

  const stageOptions = getStageOptions();
  const statusOptions = getStatusOptions(formData.report_stage, formData.report_status);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="w-full">
        {/* Main Content */}
        <div className="slide-up">
          <form onSubmit={handleSubmit}>
            {(isReportCompleted || isIdentityFieldsLocked) && (
              <InfoMessage
                message={
                  isReportCompleted ? completedReportLockedMessage : identityFieldsLockedMessage
                }
              />
            )}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-3">
                <Label htmlFor="client" className={`pl-3 text-xs font-medium uppercase ${isIdentityFieldsLocked || isEditDisabled ? "text-gray-400" : "text-gray-0"}`}>
                  Client *
                </Label>
                <Combobox
                  options={clients.map(userToComboboxOption)}
                  value={formData.client_id}
                  onValueChange={(value) => handleSelectChange("client", value)}
                  placeholder="SELECT CLIENT"
                  searchPlaceholder="Search client..."
                  disabled={isIdentityFieldsLocked || isEditDisabled}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="site" className={`pl-3 text-xs font-medium uppercase ${isIdentityFieldsLocked || isEditDisabled ? "text-gray-400" : "text-gray-0"}`}>
                  Site *
                </Label>
                <Combobox
                  options={filteredSites.map((site) => ({
                    value: site.id,
                    label: site.name,
                  }))}
                  value={formData.site_id}
                  onValueChange={(value) => handleSelectChange("site", value)}
                  placeholder="SELECT SITE"
                  searchPlaceholder="Search site..."
                  disabled={isIdentityFieldsLocked || isEditDisabled || !formData.client_id}
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="name" className={`pl-3 text-xs font-medium uppercase ${isIdentityFieldsLocked || isEditDisabled ? "text-gray-400" : "text-gray-0"}`}>
                  Report Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Report Name"
                  value={formData.report_name}
                  onChange={(e) => handleInputChange("report_name", e.target.value)}
                  disabled={isIdentityFieldsLocked || isEditDisabled}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-3">
                <Label
                  htmlFor="fieldEngineer"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled ? "text-gray-400" : "text-gray-0"}`}
                >
                  Field Engineer
                </Label>
                <Combobox
                  options={fieldEngineerList.map(userToComboboxOption)}
                  value={formData.field_engineer_id}
                  onValueChange={(value) => handleSelectChange("fieldEngineer", value)}
                  placeholder="SELECT FIELD ENGINEER"
                  searchPlaceholder="Search field engineer..."
                  disabled={isEditDisabled}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="peerReviewer"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled ? "text-gray-400" : "text-gray-0"}`}
                >
                  Peer Reviewer
                </Label>
                <Combobox
                  options={peerReviewerList.map(userToComboboxOption)}
                  value={formData.peer_reviewer_id}
                  onValueChange={(value) => handleSelectChange("peerReviewer", value)}
                  placeholder="SELECT PEER REVIEWER"
                  searchPlaceholder="Search peer reviewer..."
                  disabled={isEditDisabled}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="siteReviewer"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled ? "text-gray-400" : "text-gray-0"}`}
                >
                  Site Reviewer
                </Label>
                <Combobox
                  options={siteReviewerList.map(userToComboboxOption)}
                  value={formData.site_reviewer_id}
                  onValueChange={(value) => handleSelectChange("siteReviewer", value)}
                  placeholder="SELECT SITE REVIEWER"
                  searchPlaceholder="Search site reviewer..."
                  disabled={isEditDisabled}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="directorReviewer"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled ? "text-gray-400" : "text-gray-0"}`}
                >
                  Director Reviewer
                </Label>
                <Combobox
                  options={directorReviewerList.map(userToComboboxOption)}
                  value={formData.director_reviewer_id}
                  onValueChange={(value) => handleSelectChange("directorReviewer", value)}
                  placeholder="SELECT DIRECTOR REVIEWER"
                  searchPlaceholder="Search director reviewer..."
                  disabled={isEditDisabled}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-3">
                <Label
                  htmlFor="fieldEngineer"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled ? "text-gray-400" : "text-gray-0"}`}
                >
                  Visit Date *
                </Label>
                <Input
                  id="visit_date"
                  name="visit_date"
                  type="date"
                  value={formData.visit_date}
                  onChange={(e) => handleInputChange("visit_date", e.target.value)}
                  disabled={isEditDisabled}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="reportStage"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled || isStageSelectionDisabled() || isStageFieldDisabled() ? 'text-gray-400' : 'text-gray-0'}`}
                >
                  Report Stage:
                </Label>
                <Combobox
                  options={stageOptions}
                  value={formData.report_stage || "unallocated"}
                  onValueChange={(value) => handleSelectChange("report_stage", value)}
                  placeholder="SELECT STAGE"
                  searchPlaceholder="Search stage..."
                  disabled={isEditDisabled || isStageSelectionDisabled() || isStageFieldDisabled()}
                />
              </div>
              <div className="space-y-3">
                <Label
                  htmlFor="reportStatus"
                  className={`pl-3 text-xs font-medium uppercase ${isEditDisabled || isStageSelectionDisabled() || isStageStatusChangeDisabled() ? 'text-gray-400' : 'text-gray-0'}`}
                >
                  Report Status:
                </Label>
                <div className="flex gap-2">
                  <Combobox
                    options={statusOptions}
                    value={formData.report_status || "awaiting"}
                    onValueChange={(value) => handleInputChange("report_status", value)}
                    placeholder="SELECT STATUS"
                    searchPlaceholder="Search status..."
                    disabled={isEditDisabled || isStageSelectionDisabled() || isStageStatusChangeDisabled()}
                    className="flex-1"
                  />

                </div>

              </div>
              <div className="flex items-center gap-2 pace-y-3">
                {shouldShowDownloadButton() && (
                  <Button
                    type="button"
                    onClick={handleDownloadAndStart}
                    disabled={isDownloading}
                    className="w-full mt-8 !text-xs bg-green-600 hover:bg-green-500 sm:w-auto sm:text-base"
                  >
                    {isDownloading ? "Downloading..." : "Download"}
                  </Button>
                )}
                {shouldCompleteButton() && (
                  <Button
                    type="button"
                    onClick={handleComplete}
                    disabled={isProcessingFile}
                    className="w-full mt-8 !text-xs bg-green-600 hover:bg-green-500 sm:w-auto sm:text-base"
                  >
                    {isProcessingFile ? "Completing..." : "Complete"}
                  </Button>
                )}
                {shouldShowRejectButton() && (
                  <Button
                    type="button"
                    onClick={handleReject}
                    className="w-full mt-8 !text-xs bg-red-500 hover:bg-red-900 sm:w-auto sm:text-base"
                  >
                    Reject
                  </Button>
                )}

              </div>
            </div>

            {/* File Upload Section */}
            {shouldShowFileUpload() && (
              <div className="slide-up mb-8 grid grid-cols-[1fr] gap-4">
                <div className="space-y-3">
                  <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                    {existingFile && !isReplacingFile
                      ? "Current Report File"
                      : "Upload Report File" + (isFileUploadMandatory() ? " *" : " (Optional)")
                    }
                  </Label>

                  {/* Show existing file if available and not being replaced */}
                  {existingFile && !isReplacingFile && (
                    <div className="p-4 border border-gray-600 rounded-lg bg-navy-100">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col">
                          <span className="text-gray-0">Current File</span>
                          <span className="text-xs text-gray-400">
                            {existingFile.filename}
                            {existingFile.uploadedBy && ` (Uploaded by: ${existingFile.uploadedBy})`}
                          </span>
                        </div>
                        <div className="flex flex-col w-full gap-2 sm:w-auto sm:flex-row">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleViewExistingFile}
                            className="w-full sm:w-auto"
                          >
                            View File
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsReplacingFile(true);
                              setUploadedFile(null);
                              toast.success("You can now upload a new file to replace the existing one");
                            }}
                            className="w-full sm:w-auto"
                          >
                            Replace File
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message when file exists */}
                  {(existingFile || uploadedFile) && (
                    <div className="text-sm text-gray-100">
                      {existingFile && !isReplacingFile
                        ? "You have an existing file for this report. You can view it above or replace it with a new file."
                        : uploadedFile
                          ? "A new file is selected. You can view it below or remove it to upload a different file."
                          : "You can now upload a new file to replace the existing one."}
                    </div>
                  )}

                  {/* File Upload Area - Only show when no file exists or when replacing */}
                  {((!existingFile && !uploadedFile) || (isReplacingFile && !uploadedFile)) && (
                    <div className="space-y-4">
                      <div
                        className={`rounded-lg border-2 border-dashed bg-navy-100 p-6 text-center transition-all duration-300 ${isDragging ? "border-red-500 shadow-lg" : "border-gray-600"
                          }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragging(false);
                          if (uploadedFile) {
                            toast.error(
                              "A file is already selected. Please remove it first to upload a different file."
                            );
                            return;
                          }
                          const files = Array.from(e.dataTransfer.files);
                          if (files.length > 0) {
                            handleFiles(files);
                          }
                        }}
                      >
                        <div className="text-gray-0">
                          <p className="uppercase">
                            {isDragging ? "Drop files here" : "Drag and drop files here"}
                          </p>
                          <p className="mt-2 text-sm">or</p>
                          <input
                            type="file"
                            className="hidden"
                            id="fileInput"
                            accept={expectedFileFormat === 'pdf' ? '.pdf' : expectedFileFormat === 'docx' ? '.docx' : '.doc'}
                            onChange={(e) => {
                              if (uploadedFile) {
                                toast.error(
                                  "A file is already selected. Please remove it first to upload a different file."
                                );
                                return;
                              }
                              const files = Array.from(e.target.files);
                              if (files.length > 0) {
                                handleFiles(files);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            onClick={() => document.getElementById("fileInput").click()}
                            variant="outline"
                            className=""
                          >
                            Browse Files
                          </Button>
                          <p className="mt-2 text-xs leading-snug text-gray-400">
                            Maximum file size: 25MB. Expected format: {expectedFileFormat ? expectedFileFormat.toUpperCase() : 'DOC'} files.
                            {isFileUploadMandatory() ? " File upload is required to replace the existing file." : " File upload is optional."}
                          </p>
                        </div>
                      </div>
                      {/* Cancel replacement button when replacing */}
                      {isReplacingFile && existingFile && (
                        <div className="flex justify-end !p-0 !m-0">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={cancelFileReplacement}
                            className="p-0 text-red-600 border-red-600"
                          >
                            Cancel Replacement
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Display Uploaded File */}
                  {uploadedFile && (
                    <div className="p-2 border border-gray-600 rounded-lg bg-navy-100">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col">
                          <span className="text-gray-0">
                            {isReplacingFile ? "New File (Replacing existing)" : "Selected File"}
                          </span>
                          <span className="text-xs text-gray-400 break-all">
                            {uploadedFile.name} ({formatFileSize(uploadedFile.size)})
                          </span>
                        </div>
                        <div className="flex flex-col w-full gap-2 sm:w-auto sm:flex-row">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleViewUploadedFile(uploadedFile)}
                            className="w-full sm:w-auto"
                          >
                            View
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setUploadedFile(null);
                              if (isReplacingFile && existingFile) {
                                cancelFileReplacement();
                              } else {
                                toast.success("File removed.");
                              }
                            }}
                            className="w-full sm:w-auto"
                          >
                            {isReplacingFile && existingFile ? "Cancel Replacement" : "Remove"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {showSkeletonField && (
              <div className="slide-up mb-8 grid grid-cols-[1fr] gap-4">
                <div className="flex flex-col gap-2 space-y-3">
                  <p className="m-0 text-gray-0">
                    Skeleton reports name :{" "}
                    {selectedMineType?.file_path
                      ? selectedMineType.file_path
                        .split("/")
                        .pop()
                        .replace(/\.[^/.]+$/, "")
                      : ""}
                  </p>
                  <p className="!m-0 text-gray-0">Mine type : {selectedMineType?.name}</p>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Link className="border-r rounded-b-none border-gray-0" to="/reports">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={isEditDisabled || updateMutation.isLoading || isCheckingDuplicate || isProcessingFile}
                >
                  {isCheckingDuplicate
                    ? "Checking..."
                    : isProcessingFile
                      ? "Reading your file..."
                      : updateMutation.isLoading
                        ? "Updating..."
                        : "Update"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditReportPage;